import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { WEB_URL } from "@/lib/webUrl";
import { routes } from "@ckgo2/shared";
import { guideSchema } from "@ckgo2/shared/schemas";
import FieldHint from "@/components/common/FieldHint";
import MediaPicker from "@/components/common/MediaPicker";
import TabWebLink from "@/components/common/TabWebLink";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import EditPageHeader from "@/components/common/EditPageHeader";
import { useEditPage } from "@/hooks/useEditPage";
import DestinationPicker from "@/components/common/DestinationPicker";
import GuideInterviewEditor from "@/components/guides/GuideInterviewEditor";
import GuideTipsFields from "@/components/guides/GuideTipsFields";
import { guideHintRegistry } from "@/lib/webHints/guide";
import {
  GUIDE_FORM_DEFAULTS,
  guideFormFromDetail,
  type GuideDetail,
  type GuideFormData,
} from "./guideForm";

export default function GuideEditPage() {
  const { t } = useTranslation();
  // Formulář nese jen zobrazená pole (`guideFormFromDetail`); skryté sloupce tělo uložení
  // nenese a partial PUT je nechá v DB.
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    GuideFormData,
    GuideDetail
  >({
    resourceKey: "guide",
    listKey: "guides",
    apiPath: "/api/admin/guides",
    schema: guideSchema,
    defaultValues: GUIDE_FORM_DEFAULTS,
    transformOnLoad: guideFormFromDetail,
    navigateTo: "/guides",
    slugSourceField: "name",
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditPageHeader
        backUrl="/guides"
        isNew={isNew}
        newTitle={t("catalog.guides.edit.newTitle")}
        editTitle={t("catalog.guides.edit.editTitle")}
        entityName={itemData?.name}
        extraActions={
          // Uložená adresa a uložený stav (`itemData`), ne formulář: web ukazuje uložené.
          <TabWebLink
            target={{ anchor: null }}
            pageUrl={itemData?.slug ? `${WEB_URL}${routes.pruvodce(itemData.slug)}` : null}
            published={!isNew && itemData?.isActive === true}
            isNew={isNew}
          />
        }
        formId="guide-form"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        error={saveMutation.error}
        validationErrors={errors}
        isSuccess={saveMutation.isSuccess}
      />

      <form id="guide-form" onSubmit={handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-3">
        {/* Main content - 2 columns */}
        <div className="space-y-6 lg:col-span-2">
          {/* Basic info */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("catalog.basicInfo")}
            </h2>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="label">
                  {t("common.fields.name")} *
                </label>
                <input
                  {...register("name")}
                  type="text"
                  id="name"
                  className={`input ${errors.name ? "input-error" : ""}`}
                  placeholder={t("catalog.guides.edit.namePlaceholder")}
                />
                {errors.name && (
                  <p className="text-error-600 mt-1 text-sm">{errors.name.message}</p>
                )}
                <FieldHint registry={guideHintRegistry} field="name" variant="guide" />
              </div>

              <div>
                <label htmlFor="slug" className="label">
                  {t("catalog.urlSlug")} *
                </label>
                <input
                  {...register("slug")}
                  type="text"
                  id="slug"
                  className={`input ${errors.slug ? "input-error" : ""}`}
                  placeholder={t("catalog.guides.edit.slugPlaceholder")}
                />
                {errors.slug && (
                  <p className="text-error-600 mt-1 text-sm">{errors.slug.message}</p>
                )}
                <FieldHint registry={guideHintRegistry} field="slug" variant="guide" />
              </div>

              <div>
                <label htmlFor="yearsExperience" className="label">
                  {t("catalog.guides.edit.yearsExperience")}
                </label>
                <input
                  {...register("yearsExperience", { valueAsNumber: true })}
                  type="number"
                  id="yearsExperience"
                  className="input"
                  min={0}
                />
                <FieldHint registry={guideHintRegistry} field="yearsExperience" variant="guide" />
              </div>

              <div>
                <label htmlFor="livingIn" className="label">
                  {t("catalog.guides.edit.location")}
                </label>
                <input
                  {...register("livingIn")}
                  type="text"
                  id="livingIn"
                  className="input"
                  placeholder={t("catalog.guides.edit.locationPlaceholder")}
                />
                <FieldHint registry={guideHintRegistry} field="livingIn" variant="guide" />
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("catalog.guides.edit.bioTitle")}
            </h2>
            <div>
              <label className="label">{t("catalog.guides.edit.bioLabel")}</label>
              <Controller
                name="bio"
                control={control}
                render={({ field }) => (
                  <RichTextEditor
                    content={field.value || ""}
                    onChange={field.onChange}
                    placeholder={t("catalog.guides.edit.bioPlaceholder")}
                  />
                )}
              />
              <FieldHint registry={guideHintRegistry} field="bio" variant="guide" />
            </div>
          </div>

          {/* Specializations */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("catalog.guides.edit.specializations")}
            </h2>
            <div>
              <label htmlFor="specializations" className="label">
                {t("catalog.guides.edit.specializations")}
              </label>
              <textarea
                id="specializations"
                rows={3}
                className="input"
                placeholder={t("catalog.guides.edit.specializationsPlaceholder")}
                value={watch("specializations")?.join(", ") ?? ""}
                onChange={(e) => {
                  const value = e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean);
                  setValue("specializations", value);
                }}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t("catalog.guides.edit.specializationsHint")}
              </p>
              <FieldHint registry={guideHintRegistry} field="specializations" variant="guide" />
            </div>
          </div>

          {/* Rozhovor „Ptali jsme se“ (prostý text, pořadí redakce) */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("guideProfile.interview.title")}
            </h2>
            <Controller
              name="interview"
              control={control}
              render={({ field }) => (
                <GuideInterviewEditor
                  value={field.value}
                  onChange={field.onChange}
                  errors={errors.interview}
                />
              )}
            />
            <FieldHint registry={guideHintRegistry} field="interview" variant="guide" />
          </div>

          {/* Tipy: tři pole, prázdné tipy = null */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("guideProfile.tips.title")}
            </h2>
            <Controller
              name="tips"
              control={control}
              render={({ field }) => (
                <GuideTipsFields value={field.value} onChange={field.onChange} />
              )}
            />
            <FieldHint registry={guideHintRegistry} field="tips" variant="guide" />
          </div>

          {/* Galerie profilu: bez hlavní fotky (`primary`) i bez účelu hero, průvodce má portrét */}
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("guideProfile.gallery.title")}
            </h2>
            <Controller
              name="galleryImageIds"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  mode="multiple"
                  folder="guides"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <FieldHint registry={guideHintRegistry} field="galleryImageIds" variant="guide" />
          </div>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("catalog.photo")}
            </h2>
            <Controller
              name="photoMediaId"
              control={control}
              render={({ field }) => (
                <MediaPicker
                  mode="single"
                  folder="guides"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <FieldHint registry={guideHintRegistry} field="photoMediaId" variant="guide" />
          </div>

          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
              {t("common.settings")}
            </h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="countryIds" className="label">
                  {t("guideProfile.countries.label")}
                </label>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  {t("guideProfile.countries.help")}
                </p>
                {/* Země, kde průvodce provází (jen zemská úroveň, pořadí výběru). První zemi
                    server zrcadlí do domovské destinace, kterou čte harmonogram. */}
                <Controller
                  name="countryIds"
                  control={control}
                  render={({ field }) => (
                    <DestinationPicker
                      id="countryIds"
                      mode="multiple"
                      level="country"
                      value={field.value}
                      onChange={field.onChange}
                      ref={field.ref}
                      invalid={!!errors.countryIds}
                    />
                  )}
                />
                <FieldHint registry={guideHintRegistry} field="countryIds" variant="guide" />
              </div>

              <div>
                <label htmlFor="sortOrder" className="label">
                  {t("common.fields.sortOrder")}
                </label>
                <input
                  {...register("sortOrder", { valueAsNumber: true })}
                  type="number"
                  id="sortOrder"
                  className="input"
                  min={0}
                />
                <FieldHint registry={guideHintRegistry} field="sortOrder" variant="guide" />
              </div>

              <div>
                <div className="flex items-center gap-3">
                  <input
                    {...register("isActive")}
                    type="checkbox"
                    id="isActive"
                    className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                  />
                  <label
                    htmlFor="isActive"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {t("catalog.guides.edit.activeGuide")}
                  </label>
                </div>
                <FieldHint registry={guideHintRegistry} field="isActive" variant="guide" />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
